import { FC, useEffect, useRef, ReactNode, useState, useMemo } from 'react';
import {
  RiBookmark3Line,
  RiCheckboxCircleFill,
  RiFontSize,
  RiLinksFill,
  RiNumber6,
  RiTaskLine,
} from '@remixicon/react';
import { useAtomValue as useStateSegmentValue } from 'jotai';
import { useLocation } from 'react-router-dom';
import has from 'lodash/has';
import get from 'lodash/get';
import { emitCustomEvent } from 'react-custom-events';

import styles from './ContentQualityCheck.module.css';
import { translate } from '../../../localization';
import { PAGE_ROUTES } from '../../../constants/common';
import QualityCheckIcon from '../../atoms/QualityCheckIcon';

import { GenerateContentValidationStateSegment } from '../../../store/markettingContentGeneration/stateSegments/GenerateContentValidationStateSegment';

import ReviewSuggestedIcon from '../../atoms/ReviewSuggestedIcon';
import BoldIcon from '../../atoms/BoldIcon';
import QualityCheckNeedsReviewIcon from '../../atoms/QualityCheckNeedsReviewIcon';
import QcReview from '../QcReview';
import { QcReviewName, QualityCheckType } from '../../../types/QualityChecks';
import QcPreviewMode, { QcModules } from '../QcPreviewMode';
import {
  findContentLabelById,
  prepareModuleContentForQcReview,
} from '../../../store/markettingContentGeneration/helpers/common';
import { moduleContentsStateSegment } from '../../../store/markettingContentGeneration/stateSegments/emailModulesStateSegment';
import { CustomEventsName } from '../../../types/customEvents';
import {
  contentListStateSegment,
  selectedChannel,
  selectedDestinationFormat,
} from '../../../store/markettingContentGeneration/stateSegments/contentListSegment';
import { contentPaginationStateSegment } from '../../../store/markettingContentGeneration/stateSegments/contentPaginationStateSegment';
import LoadingWheel from '../../atoms/LoadingWheel';
import QcIconSuccess from '../../atoms/QcIconSuccess';
import { ModalVariant } from '../../molecules/ModalComponent';
import {
  ApplicableRecommendationData,
  QualityCheck,
} from '../../../types/contentValidation';
import { boostContent } from '../../../store/markettingContentGeneration/actions/microProcessActions/boostContent';
import { ParamsUsed } from '../../../types/store/markettingContentGeneration/majorProcessActions';
import QualityCheckInitialScreen, {
  SectionData,
} from '../../molecules/QualityCheckIntialScreen';

type QualityCheckProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  className?: string;
  status?: 'VALIDATED' | 'REVIEW_SUGGESTED' | 'NOT_VALIDATED';
  isApplicable?: boolean;
};

enum ValidationType {
  CLAIMS_AND_REFERENCES = 'CLAIMS_AND_REFERENCES',
  ISI_OR_PI_APPLICABILITY = 'ISI_OR_PI_APPLICABILITY',
  SPELLING_AND_GRAMMAR = 'SPELLING_AND_GRAMMAR',
  NEW_BRAND_RULE = 'NEW_BRAND_RULE',
  VERBAL_IDENTITY_GUIDELINES = 'VERBAL_IDENTITY_GUIDELINES',
  CHANNEL_BEST_PRACTICES = 'CHANNEL_BEST_PRACTICES',
}

interface QualityCheckItem {
  icon: ReactNode;
  title: string;
  description: string;
  type: ValidationType;
}

type ContentQualityCheckHeaderProps = {
  contentTag?: string;
  heading?: string;
  description?: string;
  icon?: ReactNode;
};

function convertValidationItems(data: QualityCheck[]): QualityCheckType {
  const result: QualityCheckType = {};

  data.forEach(({ type, category, name, validations: { status, summary } }) => {
    if (!result[status]) {
      result[status] = {};
    }

    if (!result[status][category]) {
      result[status][category] = [];
    }

    result[status][category].push({ type, name, summary: summary ?? '' });
  });

  return result;
}

const ContentQualityCheckHeader: FC<ContentQualityCheckHeaderProps> = ({
  contentTag,
  heading,
  description,
  icon,
}) => {
  return (
    <div className={styles['content-quality-check-header']}>
      <div className={styles['content-quality-check-header__wrapper']}>
        {contentTag && (
          <h6 className={styles['content-quality-check-header__content-tag']}>
            {contentTag}
          </h6>
        )}
        {heading && (
          <h1
            className={styles['content-quality-check-header__wrapper__heading']}
            tabIndex={0}
            onKeyUp={() => {}}
            role='none'
          >
            {heading}
          </h1>
        )}
        {description && (
          <p
            className={
              styles['content-quality-check-header__wrapper__description']
            }
          >
            {description}
          </p>
        )}
      </div>
      {icon && <div>{icon}</div>}
    </div>
  );
};

const QualityCheckLabel: FC<QualityCheckProps> = ({
  icon,
  title,
  description,
  className = '',
  status = '',
  isApplicable = false,
}) => {
  const badgeStyle = {
    VALIDATED: {
      label: 'Validated',
      className: `${styles['quality-check__status-chip']} ${styles['quality-check__status-chip--validated']}`,
    },
    REVIEW_SUGGESTED: {
      label: 'Review suggested',
      className: `${styles['quality-check__status-chip']} ${styles['quality-check__status-chip--review-suggested']}`,
    },
    CHECK_DOES_NOT_APPLY: {
      label: 'Check Does Not Apply',
      className: `${styles['quality-check__status-chip']} ${styles['quality-check__status-chip--check-does_not_apply']}`,
    },
  };

  return (
    <div className={`${className || ''} ${styles['quality-check']}`}>
      <div className={styles.icon__wrapper}>
        {icon}

        {status === 'VALIDATED' && isApplicable && (
          <div className={styles['checked-icon']}>
            <RiCheckboxCircleFill size={24} />
          </div>
        )}

        {status === 'REVIEW_SUGGESTED' && isApplicable && (
          <div
            className={`${styles['checked-icon']} ${styles['checked-icon--review-suggested']}`}
          >
            <ReviewSuggestedIcon />
          </div>
        )}
      </div>

      <div className={styles['quality-check__details']}>
        <h3 className={styles.title}>{title}</h3>
        {status &&
          (status === 'VALIDATED' || status === 'REVIEW_SUGGESTED') &&
          isApplicable && (
            <div className={badgeStyle[status].className}>
              {badgeStyle[status].label}
            </div>
          )}

        {status && status === 'NOT_VALIDATED' && !isApplicable && (
          <div className={badgeStyle.CHECK_DOES_NOT_APPLY.className}>
            {badgeStyle.CHECK_DOES_NOT_APPLY.label}
          </div>
        )}
        <p className={styles.description}>{description}</p>
      </div>
    </div>
  );
};

const ContentQualityCheck = ({
  contentId = '',
  isEmailPreviewMode = false,
  isOnGenerateContentRoute = true,
  onSizeChange,
  onClose,
}: {
  contentId: string;
  isEmailPreviewMode?: boolean;
  isOnGenerateContentRoute?: boolean;
  onSizeChange: (value: ModalVariant) => void;
  onClose: () => void;
}) => {
  const location = useLocation();
  const isNotGenerateContentRoute = useMemo(() => {
    return isOnGenerateContentRoute !== undefined
      ? !isOnGenerateContentRoute
      : location.pathname !== PAGE_ROUTES.GENERATE_CONTENT;
  }, [isOnGenerateContentRoute, location.pathname]);

  const retryParams = useRef<{
    contentId: string;
    sessionId: string;
    contentGroupId: string;
    action: string;
  }>({ contentId: '', sessionId: '', contentGroupId: '', action: '' });

  const generateContentValidation = useStateSegmentValue(
    GenerateContentValidationStateSegment
  );

  const currentContentValidation = generateContentValidation[contentId];

  const modulesContentSegment = useStateSegmentValue(
    moduleContentsStateSegment
  );

  const selectedChannelSegment = useStateSegmentValue(selectedChannel);
  const selectedDestinationFormatSegment = useStateSegmentValue(
    selectedDestinationFormat
  );

  const contentListState = useStateSegmentValue(contentListStateSegment);
  const currentViewVersion = useStateSegmentValue(
    contentPaginationStateSegment
  );

  const { retryPromptParams, contentList: contentItems } = contentListState;

  const [emailPreviewData, setEmailPreviewData] = useState<QcModules[]>([]);
  const [applicableRecommendationList, setApplicableRecommendationList] =
    useState<ApplicableRecommendationData[]>([]);

  const [qcHeaderConfig, setQcHeaderConfig] =
    useState<ContentQualityCheckHeaderProps>({
      contentTag: '',
      heading: translate('quality_check_validation'),
      description: translate('quality_check_description'),
      icon: <QualityCheckIcon />,
    });

  const hasQualityValidationData = has(generateContentValidation, contentId);
  const isQualityValidationLoading =
    hasQualityValidationData && currentContentValidation.isLoading;

  const hasQualityValidationInitialized =
    Object.keys(generateContentValidation).length > 0;

  const isRecommendationAvailable =
    hasQualityValidationData &&
    currentContentValidation.applicableRecommendations &&
    currentContentValidation.applicableRecommendations.length > 0;

  const shouldShowRecommendations = isRecommendationAvailable === true;

  const shouldShowLoading =
    isQualityValidationLoading ||
    (hasQualityValidationInitialized === true &&
      contentId.length === 0 &&
      isEmailPreviewMode === false);

  enum QualityCheckCategories {
    COMPLIANCE = 'Compliance',
    CONTENT = 'Content',
    BRAND = 'Brand',
  }

  const qualityChecks = {
    [QualityCheckCategories.COMPLIANCE]: [
      {
        icon: <RiTaskLine size={18} />,
        title: translate('claims_and_references'),
        description: translate('claims_and_references_validation_info'),
        type: ValidationType.CLAIMS_AND_REFERENCES,
      },
      {
        icon: <RiNumber6 size={18} />,
        title: translate('six_month_rule'),
        description: translate('six_month_rule_validation_info'),
        type: ValidationType.NEW_BRAND_RULE,
      },
      {
        icon: <RiLinksFill size={18} />,
        title: translate('isi_and_pi'),
        description: translate('isi_and_pi_validation_info'),
        type: ValidationType.ISI_OR_PI_APPLICABILITY,
      },
    ],
    [QualityCheckCategories.CONTENT]: [
      {
        icon: <RiFontSize size={18} />,
        title: translate('spelling_grammar'),
        description: translate('spelling_grammar_validation_info'),
        type: ValidationType.SPELLING_AND_GRAMMAR,
      },
      {
        icon: <RiBookmark3Line size={18} />,
        title: translate('channel_best_practices'),
        description: translate('channel_best_practices_validation_info'),
        type: ValidationType.CHANNEL_BEST_PRACTICES,
      },
    ],
    [QualityCheckCategories.BRAND]: [
      {
        icon: <BoldIcon />,
        title: translate('pfizer_brand_verbal_identity'),
        description: translate('pfizer_brand_verbal_identity_validation_info'),
        type: ValidationType.VERBAL_IDENTITY_GUIDELINES,
      },
    ],
  };

  // Convert qualityChecks to sectionsData format for CSS-only layout
  const sectionsData: SectionData[] = Object.entries(qualityChecks).map(
    ([category, checks]) => ({
      id: category.toLowerCase(),
      title: category,
      cards: (checks as QualityCheckItem[]).map(check => ({
        id: check.type,
        content: (
          <QualityCheckLabel
            key={check.type}
            icon={check.icon}
            title={check.title}
            description={check.description}
          />
        ),
      })),
    })
  );

  const handleModuleReviewClick = (moduleKey: string) => {
    emitCustomEvent(CustomEventsName.LOAD_EMAIL_MODULE, moduleKey);
  };

  const handleRecommendationToggle = (recommendationId: string) => {
    const recommendations = [...applicableRecommendationList];
    // Find the recommendation by its ID
    const recommendation = recommendations.find(
      item => item.recommendationId === recommendationId
    );

    // If the recommendation is found, toggle its isSelected property
    if (recommendation) {
      recommendation.isSelected = !recommendation.isSelected;
    }

    setApplicableRecommendationList([...recommendations]);
  };

  const handleOnBoostContent = () => {
    const selectedQualityChecks = [...applicableRecommendationList]
      .filter(rec => rec.isSelected) // Filter for `isSelected: true`
      .map(({ isSelected: _, ...otherProps }) => otherProps); //

    // Get all refine-related parameters from current content item (current view version)
    const currentTone = get(
      contentItems[currentViewVersion],
      'paramsUsed.tone.name',
      ''
    );
    const currentToneLevel = get(
      contentItems[currentViewVersion],
      'paramsUsed.toneLevel.name',
      ''
    );
    const currentContext = get(
      contentItems[currentViewVersion],
      'paramsUsed.context',
      ''
    );
    const currentContentStyle = get(
      contentItems[currentViewVersion],
      'paramsUsed.contentStyle.name',
      ''
    );
    const currentInclusions = get(
      contentItems[currentViewVersion],
      'paramsUsed.inclusions',
      ''
    );
    const currentExclusions = get(
      contentItems[currentViewVersion],
      'paramsUsed.exclusions',
      ''
    );
    const currentDaring = get(
      contentItems[currentViewVersion],
      'paramsUsed.daring',
      false
    );
    const currentCompassionate = get(
      contentItems[currentViewVersion],
      'paramsUsed.compassionate',
      false
    );

    const paramUsed = {
      ...retryPromptParams,
      currentSelectedApplicableRecommendation: selectedQualityChecks,
      currentSelectedTone: currentTone,
      currentSelectedToneLevel: currentToneLevel,
      currentContextValue: currentContext,
      currentSelectedContentStyle: currentContentStyle,
      currentSelectedInclusion: currentInclusions,
      currentSelectedExclusion: currentExclusions,
      currentSelectedDaring: currentDaring,
      currentSelectedCompassionate: currentCompassionate,
    };
    boostContent(paramUsed as unknown as ParamsUsed, contentId);
    onClose();
  };

  const previewModeNeedsReview = () => {
    const emailModulePreviewData = prepareModuleContentForQcReview(
      modulesContentSegment,
      generateContentValidation
    );
    return emailModulePreviewData.some(
      item => item.status === QcReviewName.NEEDS_REVIEW
    );
  };

  useEffect(() => {
    if (currentContentValidation?.retryParams) {
      retryParams.current = currentContentValidation.retryParams;
    }

    return () => {};
  }, [generateContentValidation, contentId]);

  useEffect(() => {
    if (isRecommendationAvailable) {
      const applicableRecommendations = JSON.parse(
        JSON.stringify(currentContentValidation.applicableRecommendations)
      );
      setApplicableRecommendationList(applicableRecommendations);
    }
  }, [isRecommendationAvailable]);

  useEffect(() => {
    if (isEmailPreviewMode) {
      const emailModulePreviewData = prepareModuleContentForQcReview(
        modulesContentSegment,
        generateContentValidation
      );
      setEmailPreviewData(emailModulePreviewData);
    }
  }, [isEmailPreviewMode]);

  useEffect(() => {
    // Set initial header config for non-generate-content routes
    if (isNotGenerateContentRoute) {
      setQcHeaderConfig({
        contentTag: '',
        heading: translate('quality_check_validation'),
        description: translate('quality_check_description'),
        icon: <QualityCheckIcon />,
      });
      return; // Exit early to avoid other header configs
    }

    // quality checks loading
    if (
      isQualityValidationLoading ||
      (hasQualityValidationInitialized === true &&
        contentId.length === 0 &&
        isEmailPreviewMode === false)
    ) {
      setQcHeaderConfig({
        contentTag: '',
        heading: translate('quality_check_validation'),
        description: '',
        icon: <QualityCheckIcon />,
      });
      onSizeChange(ModalVariant.Small);
    }
    // needs review header
    if (
      hasQualityValidationData &&
      isQualityValidationLoading === false &&
      currentContentValidation.status === QcReviewName.NEEDS_REVIEW &&
      isEmailPreviewMode === false
    ) {
      if (
        selectedChannelSegment.toLowerCase() === 'email' ||
        selectedChannelSegment.toLowerCase() === 'rte' ||
        selectedDestinationFormatSegment.toLowerCase() === 'email' ||
        selectedDestinationFormatSegment.toLowerCase() === 'rte'
      ) {
        const emailModuleLabel = findContentLabelById(
          contentId,
          modulesContentSegment
        );
        setQcHeaderConfig({
          contentTag: emailModuleLabel ?? '',
          heading: translate('content_needs_review'),
          description: translate('content_needs_review_description'),
          icon: <QualityCheckNeedsReviewIcon />,
        });
      } else {
        setQcHeaderConfig({
          contentTag: '',
          heading: translate('content_needs_review'),
          description: translate('content_needs_review_description'),
          icon: <QualityCheckNeedsReviewIcon />,
        });
      }

      onSizeChange(ModalVariant.Small);
    }

    // qc passed header
    if (
      hasQualityValidationData &&
      isQualityValidationLoading === false &&
      currentContentValidation.status !== QcReviewName.NEEDS_REVIEW &&
      isEmailPreviewMode === false
    ) {
      if (
        selectedChannelSegment.toLowerCase() === 'email' ||
        selectedChannelSegment.toLowerCase() === 'rte' ||
        selectedDestinationFormatSegment.toLowerCase() === 'email' ||
        selectedDestinationFormatSegment.toLowerCase() === 'rte'
      ) {
        const emailModuleLabel = findContentLabelById(
          contentId,
          modulesContentSegment
        );
        setQcHeaderConfig({
          contentTag: emailModuleLabel ?? '',
          heading: translate('qc_content_looks_great'),
          description: translate('qc_content_passed_description'),
          icon: <QcIconSuccess />,
        });
      } else {
        setQcHeaderConfig({
          contentTag: '',
          heading: translate('qc_content_looks_great'),
          description: translate('qc_content_passed_description'),
          icon: <QcIconSuccess />,
        });
      }

      onSizeChange(ModalVariant.Small);
    }

    // qc preview mode needs review
    if (isEmailPreviewMode === true && previewModeNeedsReview() === true) {
      setQcHeaderConfig({
        contentTag: translate('email_summary'),
        heading: translate('content_needs_review'),
        description: translate('content_needs_review_description_preview_mode'),
        icon: <QualityCheckNeedsReviewIcon />,
      });

      onSizeChange(ModalVariant.Small);
    }

    // qc preview mode passed
    if (isEmailPreviewMode === true && previewModeNeedsReview() === false) {
      setQcHeaderConfig({
        contentTag: translate('email_summary'),
        heading: translate('qc_content_looks_great'),
        description: translate('qc_content_passed_description'),
        icon: <QcIconSuccess />,
      });

      onSizeChange(ModalVariant.Small);
    }
  }, [
    isNotGenerateContentRoute,
    isEmailPreviewMode,
    contentId,
    isQualityValidationLoading,
    hasQualityValidationData,
  ]);

  useEffect(() => {
    if (isRecommendationAvailable) {
      onSizeChange(ModalVariant.Medium);
    }
  }, [isRecommendationAvailable]);

  return (
    <div className={styles['quality-check__page-wrapper']}>
      {/* Show initial screen when not on GENERATE_CONTENT route */}
      {isNotGenerateContentRoute && (
        <>
          <ContentQualityCheckHeader
            heading={qcHeaderConfig.heading}
            description={qcHeaderConfig.description}
            icon={qcHeaderConfig.icon}
          />
          <div className={styles['quality-check__category-content-wrapper']}>
            <QualityCheckInitialScreen
              sections={sectionsData}
              layoutType='grid'
            />
          </div>
        </>
      )}

      {/* Show initial screen when quality validation not initialized and on GENERATE_CONTENT route */}
      {!isNotGenerateContentRoute &&
        hasQualityValidationInitialized === false && (
          <>
            <ContentQualityCheckHeader
              heading={qcHeaderConfig.heading}
              description={qcHeaderConfig.description}
              icon={qcHeaderConfig.icon}
            />
            <div className={styles['quality-check__category-content-wrapper']}>
              <QualityCheckInitialScreen
                sections={sectionsData}
                layoutType='grid'
              />
            </div>
          </>
        )}

      {/* Rest of the existing logic for GENERATE_CONTENT route */}
      {!isNotGenerateContentRoute &&
        hasQualityValidationData &&
        isQualityValidationLoading === false &&
        isEmailPreviewMode === false && (
          <div>
            <ContentQualityCheckHeader
              contentTag={qcHeaderConfig.contentTag}
              heading={qcHeaderConfig.heading}
              description={qcHeaderConfig.description}
              icon={qcHeaderConfig.icon}
            />
            <div className={styles['content-quality-check__content--area']}>
              <QcReview
                recommendationsData={applicableRecommendationList}
                qualityChecksData={convertValidationItems(
                  currentContentValidation.qualityChecks
                )}
                isRecommendationVisible={shouldShowRecommendations}
                onContentBoost={handleOnBoostContent}
                onRecommendationToggle={handleRecommendationToggle}
              />
            </div>
          </div>
        )}

      {!isNotGenerateContentRoute && shouldShowLoading ? (
        <div>
          <ContentQualityCheckHeader
            heading={qcHeaderConfig.heading}
            icon={qcHeaderConfig.icon}
          />
          <div className={styles['quality-check__loading--area']}>
            <LoadingWheel size={32} />
            <p>{translate('quality_check_loading')}</p>
          </div>
        </div>
      ) : null}

      {!isNotGenerateContentRoute && isEmailPreviewMode && (
        <div>
          <ContentQualityCheckHeader
            contentTag={qcHeaderConfig.contentTag}
            heading={qcHeaderConfig.heading}
            description={qcHeaderConfig.description}
            icon={qcHeaderConfig.icon}
          />
          <div className={styles['content-quality-check__content--area']}>
            <QcPreviewMode
              modulesData={emailPreviewData}
              onReviewModuleClick={handleModuleReviewClick}
            />
          </div>
        </div>
      )}
    </div>
  );
};

ContentQualityCheck.displayName = 'ContentQualityCheck';

export default ContentQualityCheck;
